/**
 * Belote scoring and match end (rules §8, §9, §5.1).
 *
 * Both variants start from the same raw count per side: card points in the
 * tricks it won (152 in the deck), ten for the last trick, the declarations if
 * it won that comparison, and twenty for Belote-Rebelote. A capot replaces the
 * whole count with 252 for the side that took all eight.
 *
 * Classic: the taker's side must simply outscore the other. Made, both keep
 * their raw count; failed ("dedans"), the defence takes a flat 162 and every
 * declaration; exactly tied, the litige holds the taker's points for whoever
 * wins the next hand.
 *
 * Coinche: the takers must reach their bid and outscore the defence. The bid is
 * added to the winner's score, and a coinche or surcoinche multiplies the
 * contract's side of it by two or four. Belote-Rebelote is never multiplied
 * and never lost. Scores round to the nearest ten.
 */
import { SUIT_LABEL, rankOf, suitOf } from '../../core/cards.js';
import { BELOTE_CAPOT_VALUE, BELOTE_TRICKS_PER_HAND } from './config.js';
import { beloteHolder, cardsDealt, resolveDeclarations } from './declarations.js';
import { belotePoints } from './rules.js';
const KIND_NAME = {
    tierce: 'Tierce', quarte: 'Quarte', quinte: 'Quinte', carre: 'Carré',
};
const CARRE_OF = { 11: 'Jacks', 9: 'Nines', 14: 'Aces', 10: 'Tens', 13: 'Kings', 12: 'Queens' };
export function declarationText(d, trump) {
    if (d.kind === 'carre')
        return `Carré of ${CARRE_OF[rankOf(d.cards[0]) ?? 0] ?? ''}`;
    const suit = suitOf(d.cards[0]);
    return `${KIND_NAME[d.kind]} ${suit ? SUIT_LABEL[suit] : ''}${suit === trump ? ' (trump)' : ''}`;
}
/** Halves round up: 85 → 90, 84 → 80 (rules §9.8, edge #18). */
export function roundTen(n) {
    return Math.floor((n + 5) / 10) * 10;
}
export function beloteTally(hand, table, config) {
    const trump = hand.contract.trump;
    const cardPoints = table.teams.map(() => 0);
    const tricksByTeam = table.teams.map(() => 0);
    for (const t of hand.tricks) {
        if (t.winner === null)
            continue;
        const team = table.teamOf[t.winner];
        tricksByTeam[team] += 1;
        for (const p of t.plays)
            cardPoints[team] += belotePoints(p.card, trump);
    }
    const last = hand.tricks[BELOTE_TRICKS_PER_HAND - 1]?.winner ?? null;
    const capot = tricksByTeam.findIndex((n) => n === BELOTE_TRICKS_PER_HAND);
    const dealt = cardsDealt(hand.tricks, hand.secret.holdings);
    const holder = beloteHolder(dealt, trump);
    return {
        cardPoints,
        lastTrickTeam: last === null ? null : table.teamOf[last],
        capotTeam: capot === -1 ? null : capot,
        decl: config.declarations
            ? resolveDeclarations(dealt, trump, hand.dealer, table)
            : { team: null, declarations: [], points: 0 },
        beloteTeam: holder === null ? null : table.teamOf[holder],
    };
}
export function beloteScoreHand(hand, table, config, matchExtra) {
    const tricksBySeat = Array.from({ length: table.seatCount }, () => 0);
    for (const t of hand.tricks)
        if (t.winner !== null)
            tricksBySeat[t.winner] += 1;
    const reserve = matchExtra?.litige ?? 0;
    const contract = hand.contract;
    const deltas = table.teams.map(() => 0);
    if (!contract) {
        return {
            deltas, tricksBySeat, contractMade: null, lines: [],
            matchExtra: { litige: reserve, lastHand: null },
        };
    }
    const trump = contract.trump;
    const tally = beloteTally(hand, table, config);
    const { cardPoints, lastTrickTeam, capotTeam, decl, beloteTeam } = tally;
    const T = table.teamOf[contract.taker];
    const D = table.teams.find((t) => t.id !== T).id;
    const bel = (t) => (beloteTeam === t ? 20 : 0);
    const declOf = (t) => (decl.team === t ? decl.points : 0);
    const raw = (t) => (capotTeam === t ? 252 : capotTeam !== null ? 0 : cardPoints[t] + (lastTrickTeam === t ? 10 : 0)) + declOf(t) + bel(t);
    // The ledger: each side's lines, in the order they happened, closed with a
    // balancing line so they always sum to the side's delta.
    const lines = [];
    const trickLines = (t) => {
        if (capotTeam === t)
            lines.push({ team: t, text: 'Capot: all eight tricks', points: 252 });
        else if (capotTeam === null) {
            if (cardPoints[t] > 0)
                lines.push({ team: t, text: 'Cards', points: cardPoints[t] });
            if (lastTrickTeam === t)
                lines.push({ team: t, text: 'Dix de der', points: 10 });
        }
    };
    const declLines = (t, all = false) => {
        for (const d of decl.declarations) {
            if (all || decl.team === t)
                lines.push({ team: t, text: declarationText(d, trump), points: d.points });
        }
    };
    const beloteLine = (t) => {
        if (beloteTeam === t)
            lines.push({ team: t, text: 'Belote-Rebelote', points: 20 });
    };
    const close = (t, total, text) => {
        const sum = lines.filter((l) => l.team === t).reduce((n, l) => n + l.points, 0);
        if (sum !== total)
            lines.push({ team: t, text, points: total - sum });
    };
    let made;
    let litige = 0;
    const score = table.teams.map(() => 0);
    if (config.variant === 'classic') {
        if (capotTeam !== null) {
            // Rules §8.4: the capot side takes 252 and every declaration.
            const W = capotTeam;
            const L = W === T ? D : T;
            made = W === T;
            score[W] = 252 + decl.points + bel(W);
            score[L] = bel(L);
            trickLines(W);
            declLines(W, true);
            beloteLine(W);
            beloteLine(L);
        }
        else if (raw(T) > raw(D)) {
            made = true;
            score[T] = raw(T);
            score[D] = raw(D);
            for (const t of [T, D]) {
                trickLines(t);
                declLines(t);
                beloteLine(t);
            }
        }
        else if (raw(T) === raw(D)) {
            // Rules §8.2: litige. The defence scores; the taker's count waits.
            made = null;
            litige = raw(T);
            score[D] = raw(D);
            trickLines(D);
            declLines(D);
            beloteLine(D);
            lines.push({ team: T, text: `Litige: ${litige} held for the next hand`, points: 0 });
        }
        else {
            // Rules §8.3: dedans. Flat 162 and all declarations to the defence.
            made = false;
            score[T] = bel(T);
            score[D] = 162 + decl.points + bel(D);
            beloteLine(T);
            lines.push({ team: D, text: `${contract.label} went down`, points: 162 });
            declLines(D, true);
            beloteLine(D);
        }
        if (config.roundScores)
            for (const t of [T, D])
                score[t] = roundTen(score[t]);
    }
    else {
        const bid = contract.bid === 'capot' ? BELOTE_CAPOT_VALUE : (contract.bid ?? 0);
        const success = contract.bid === 'capot'
            ? capotTeam === T
            : raw(T) >= bid && raw(T) > raw(D);
        made = success;
        const mult = contract.mult;
        const bidText = contract.bid === 'capot' ? 'Capot bid' : `Bid ${bid}`;
        if (success) {
            // A taker's capot takes the defence's declarations too.
            const takerDecl = capotTeam === T ? decl.points : declOf(T);
            if (mult === 1) {
                score[T] = raw(T) - declOf(T) + takerDecl + bid;
                score[D] = capotTeam === T ? bel(D) : raw(D);
                trickLines(T);
                declLines(T, capotTeam === T);
                beloteLine(T);
                lines.push({ team: T, text: bidText, points: bid });
                if (capotTeam !== T) {
                    trickLines(D);
                    declLines(D);
                }
                beloteLine(D);
            }
            else {
                // Rules §9.3: a flat 160 (250 on a capot) plus everything owed,
                // multiplied; the defence keeps only its Belote.
                const base = capotTeam === T ? 250 : 160;
                score[T] = (base + takerDecl + bel(T) + bid) * mult;
                score[D] = bel(D);
                lines.push({ team: T, text: capotTeam === T ? 'Capot' : 'Contract made', points: base });
                declLines(T, capotTeam === T);
                beloteLine(T);
                lines.push({ team: T, text: bidText, points: bid });
                beloteLine(D);
            }
        }
        else {
            // Rules §9.4–9.5: 160 (252 on the defence's own capot), every
            // declaration and the bid go to the defence, multiplied; Belote stays
            // with whoever held it, unmultiplied.
            const base = capotTeam === D ? 252 : 160;
            score[T] = bel(T);
            score[D] = (base + decl.points + bid) * mult + bel(D);
            beloteLine(T);
            lines.push({ team: D, text: `${contract.label} went down`, points: base });
            declLines(D, true);
            lines.push({ team: D, text: bidText, points: bid });
            beloteLine(D);
        }
        if (mult > 1) {
            for (const t of [T, D])
                close(t, score[t], mult === 4 ? 'Surcoinched ×4' : 'Coinched ×2');
        }
        if (config.roundScores)
            for (const t of [T, D])
                score[t] = roundTen(score[t]);
    }
    for (const t of [T, D])
        close(t, score[t], 'Rounded');
    // A litige held from last hand goes to whoever wins this one.
    let carry = reserve;
    if (reserve > 0 && made !== null) {
        const winner = made ? T : D;
        score[winner] += reserve;
        lines.push({ team: winner, text: 'Litige from last hand', points: reserve });
        carry = 0;
    }
    for (const t of [T, D])
        deltas[t] = score[t];
    return {
        deltas,
        tricksBySeat,
        contractMade: made,
        lines,
        matchExtra: {
            litige: carry + litige,
            lastHand: { capot: capotTeam !== null, beloteTeam },
        },
    };
}
/**
 * First to the target; if both cross on the same hand the higher total wins,
 * and a tie plays one more hand. Nobody wins on a capot hand that only crossed
 * the line with its Belote (rules §5.1, edge #6): one more hand.
 */
export function beloteMatchEnd(state, config) {
    const scores = state.scores;
    const over = state.table.teams.filter((t) => (scores[t.id] ?? 0) >= config.target);
    if (over.length === 0)
        return { finished: false };
    const best = over.reduce((a, b) => ((scores[b.id] ?? 0) > (scores[a.id] ?? 0) ? b : a));
    if (over.some((t) => t.id !== best.id && scores[t.id] === scores[best.id]))
        return { finished: false };
    const last = state.extra?.lastHand;
    if (last?.capot && last.beloteTeam === best.id && (scores[best.id] ?? 0) - 20 < config.target) {
        return { finished: false };
    }
    return { finished: true, winningTeam: best.id, reason: `Reached ${scores[best.id]}` };
}
//# sourceMappingURL=scoring.js.map